import React, { useState } from 'react';
import { LocationPicker } from "./LocationPicker";
import imageCompression from 'browser-image-compression';
import { v4 as uuidv4 } from 'uuid';
import { safeFetch } from "./App";
import { Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AiDescriptionDialog from './components/AiDescriptionDialog';


export const EditBusinessForm = ({
    business,
    chatId,
    onCancel,
    onSave,
    allCategories,
    allZones,
    pendingZones = [],
}) => {
    // Helper to normalize zone entries (string or object) into a comparable string
    const zoneToString = (zone) => {
        if (typeof zone === 'string') return zone;
        if (zone && typeof zone === 'object') {
            return (
                zone.zone_display_name ||
                zone.zone_name ||
                zone.name ||
                zone.zone_id ||
                ''
            );
        }
        return '';
    };

    // State initialization order: type must come before entityLabel
    const [type, setType] = useState(business.type || (business.event_time ? 'event' : 'business'));
    const entityLabel = type === 'event' ? 'מפגש' : type === 'barter' ? 'בארטר' : 'עסק';
    const [name, setName] = useState(business.business_name || '');
    const [description, setDescription] = useState(business.business_description || '');
    const [number, setNumber] = useState(business.business_number || '');
    const [whatsapp, setWhatsapp] = useState(business.number_available_on_whatsapp || false);
    const [website, setWebsite] = useState(business.business_url || '');
    const [selectedCategories, setSelectedCategories] = useState(business.business_category || []);
    const [selectedCoverImage, setSelectedCoverImage] = useState(business.cover_image || '');
    const [removedImages, setRemovedImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [showCategoryLimitMsg, setShowCategoryLimitMsg] = useState(false);
    const [showZoneLimitMsg, setShowZoneLimitMsg] = useState(false);
    const [selectedZones, setSelectedZones] = useState(() => {
        // Normalize zones to always be an array of zone IDs (strings)
        const zones = business.zones || [];
        return zones.map(z => typeof z === 'string' ? z : z.zone_id);
    });
    const [customZones, setCustomZones] = useState([]);
    const [customZoneInput, setCustomZoneInput] = useState('');
    const [customCategory, setCustomCategory] = useState('');
    const [activeDays, setActiveDays] = useState(business.active_days || [0, 1, 2, 3, 4, 5, 6]);

    const [eventTime, setEventTime] = useState(
        business.event_time || ""
    );
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);

    // Founder Profile fields
    const [showFounderProfile, setShowFounderProfile] = useState(
        !!(business.founder_name || business.founder_about || business.founder_image_url)
    );
    const [founderName, setFounderName] = useState(business.founder_name || '');
    const [founderAbout, setFounderAbout] = useState(business.founder_about || '');
    const [founderImage, setFounderImage] = useState(null);
    const [founderImagePreview, setFounderImagePreview] = useState(business.founder_image_url || null);

    // For AI Description Dialog
    const [showAiDialog, setShowAiDialog] = useState(false);

    const [tmpLocation, setTmpLocation] = useState(() => {
        try {
            const parsed = JSON.parse(business.location || '{}');
            if (typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
                return parsed;
            }
        } catch (e) {
            console.warn("Invalid location format in business:", e);
        }
        return { lat: null, lng: null };
    });
    const [address, setAddress] = useState(business.address || '');

    const toggleCategory = (cat) => {
        setSelectedCategories((prev) =>
            prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
        );
    };

    const toggleRemoveImage = (url) => {
        setRemovedImages((prev) =>
            prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
        );
    };

    const handleCategoryToggle = (cat) => {
        if (selectedCategories.includes(cat)) {
            toggleCategory(cat);
        } else if (selectedCategories.length < 3) {
            toggleCategory(cat);
        } else {
            setShowCategoryLimitMsg(true);
            setTimeout(() => setShowCategoryLimitMsg(false), 2000);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setSaveSuccess(false);

        try {
            const remainingImages = business.images_urls.filter(
                (url) => !removedImages.includes(url)
            );

            let allFiles = [];

            // Process business/event images
            for (const file of images) {
                const isVideo = file.type.startsWith('video/');
                const baseName = file.name.replace(/_thumb|\.[^/.]+$/g, '');
                const uuid = uuidv4();

                if (isVideo) {
                    const videoName = `${uuid}_${file.name}`;
                    const renamedFile = new File([file], videoName, { type: file.type });
                    allFiles.push({ file: renamedFile, name: videoName, type: file.type });
                    continue;
                }

                const fullCompressed = await imageCompression(file, {
                    maxSizeMB: 0.5,
                    maxWidthOrHeight: 1280,
                    useWebWorker: true,
                    fileType: 'image/jpeg',
                });
                const fullName = `${uuid}_${baseName}.jpg`;
                const fullFile = new File([fullCompressed], fullName, { type: 'image/jpeg' });

                const thumbCompressed = await imageCompression(file, {
                    maxSizeMB: 0.1,
                    maxWidthOrHeight: 200,
                    useWebWorker: true,
                    fileType: 'image/jpeg',
                });
                const thumbName = `${uuid}_${baseName}_thumb.jpg`;
                const thumbFile = new File([thumbCompressed], thumbName, { type: 'image/jpeg' });

                allFiles.push(
                    { file: fullFile, name: fullName, type: 'image/jpeg' },
                    { file: thumbFile, name: thumbName, type: 'image/jpeg' }
                );
            }

            // Founder image change protection:
            // We only treat the founder image as changed if a new File object was selected (founderImage not null).
            // The preview may hold a dataURL of the existing image; that must NOT trigger an update.
            const founderImageChanged = !!founderImage;
            const founderImageIndex = founderImageChanged ? allFiles.length : -1;
            if (founderImageChanged) {
                const baseName = founderImage.name.replace(/\.[^/.]+$/g, '');
                const uuid = uuidv4();
                const founderCompressed = await imageCompression(founderImage, {
                    maxSizeMB: 0.3,
                    maxWidthOrHeight: 400,
                    useWebWorker: true,
                    fileType: 'image/jpeg',
                });
                const founderFileName = `${uuid}_${baseName}_founder.jpg`;
                const founderFile = new File([founderCompressed], founderFileName, { type: 'image/jpeg' });
                allFiles.push({ file: founderFile, name: founderFileName, type: 'image/jpeg' });
            }

            // 🔐 Get presigned URLs
            let uploadedImageUrls = [];
            let urls = null;
            if (allFiles.length > 0) {
                const presignRes = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/upload-urls', chatId, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        filenames: allFiles.map(f => f.name),
                        content_types: allFiles.map(f => f.type),
                        chat_id: chatId,
                    }),
                });

                const presignData = await presignRes.json();
                urls = presignData.urls;
                if (!urls || urls.length !== allFiles.length) {
                    throw new Error('Mismatch in presigned URL count');
                }

                for (let i = 0; i < allFiles.length; i++) {
                    const { file } = allFiles[i];
                    const { url, fields } = urls[i];
                    const formData = new FormData();

                    Object.entries(fields).forEach(([key, val]) => formData.append(key, val));
                    formData.append('file', file);

                    const uploadRes = await fetch(url, {
                        method: 'POST',
                        body: formData,
                    });

                    if (!uploadRes.ok) {
                        const text = await uploadRes.text();
                        console.error(`Upload failed for ${file.name}:`, text);
                        throw new Error('Upload failed');
                    }

                    uploadedImageUrls.push(`${url}/${fields.key}`);
                }
            }

            const finalImages = [...remainingImages, ...uploadedImageUrls.filter(k => !k.includes('_thumb') && !k.includes('_founder'))];

            // Determine final founder image URL:
            // If a new file was uploaded, use its S3 key. Otherwise keep original stored URL (not dataURL).
            let finalFounderImageUrl = business.founder_image_url || null;
            if (founderImageChanged && founderImageIndex >= 0 && urls && urls[founderImageIndex]) {
                finalFounderImageUrl = urls[founderImageIndex].fields.key;
            }

            // Validate custom zones don't already exist in approved zones or pending zones
            for (const customZone of customZones) {
                const trimmedCustomZone = customZone.trim().toLowerCase();

                // Check if zone already exists in approved zones
                if (allZones.some(z => zoneToString(z).trim().toLowerCase() === trimmedCustomZone)) {
                    alert(`האזור "${customZone}" כבר קיים ברשימה. אנא בחר אותו מהרשימה במקום להוסיף אזור חדש.`);
                    setLoading(false);
                    return;
                }

                // Check if zone is already waiting for approval
                if (pendingZones.some(z => zoneToString(z).trim().toLowerCase() === trimmedCustomZone)) {
                    alert(`האזור "${customZone}" כבר ממתין לאישור. אנא המתן לאישור או בחר אזור אחר.`);
                    setLoading(false);
                    return;
                }
            }

            // Validate custom category doesn't already exist
            if (customCategory && allCategories.some(c => c.toLowerCase() === customCategory.trim().toLowerCase())) {
                alert(`הקטגוריה "${customCategory}" כבר קיימת ברשימה. אנא בחר אותה מהרשימה במקום להוסיף קטגוריה חדשה.`);
                setLoading(false);
                return;
            }

            // Keep regular zones and custom zones separate
            const finalCategories = customCategory ? [...selectedCategories, customCategory.trim()] : selectedCategories;

            // Build payload, but include founder_image_url ONLY if changed or removed.
            const updated = {
                business_id: business.business_id,
                business_name: name,
                business_description: description,
                business_category: finalCategories,
                business_number: number,
                number_available_on_whatsapp: whatsapp,
                business_url: website,
                cover_image: selectedCoverImage,
                images_urls: finalImages,
                business_owner: business.business_owner,
                approved: business.approved,
                channel: business.channel,
                location: JSON.stringify(tmpLocation),
                address: address.trim() || undefined,  // Optional address field
                event_time: type === 'event' ? eventTime : null,
                zones: selectedZones,  // ONLY regular zones, NOT custom zones
                customZones: customZones.map(z => z.trim()),  // Custom zones sent separately
                active_days: type === 'business' ? activeDays : undefined,  // Only for businesses
            };

            if (showFounderProfile) {
                updated.founder_name = founderName;
                updated.founder_about = founderAbout;
                // Only send founder_image_url if a new image was uploaded.
                if (founderImageChanged) {
                    updated.founder_image_url = finalFounderImageUrl;
                }
            } else {
                // Explicitly null fields when profile is hidden (removal scenario)
                updated.founder_name = null;
                updated.founder_about = null;
                updated.founder_image_url = null;
            }

            onSave(updated);

            // Show success state
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000);
        } catch (err) {
            console.error("❌ Submit error:", err);
            alert("אירעה שגיאה בעת שמירת העסק. בדקו את התמונות או הווידאו ונסו שוב.");
        } finally {
            setLoading(false);
        }
    };

    // const filteredZones = allZones.filter((zone) =>
    //     zone.toLowerCase().includes(zoneSearch.toLowerCase())
    // );

    return (
        <div className="bg-surface p-6 rounded-3xl text-ink-strong space-y-6 font-[Rubik,Inter,sans-serif] max-w-3xl mx-auto shadow-card" dir="rtl">
            <div className="text-center mb-6">
                <h2 className="text-2xl font-semibold text-ink-strong">עריכת {entityLabel}</h2>
                <p className="text-sm text-ink-muted mt-1">עדכן את הפרטים ושמור שינויים</p>
            </div>

            {/* Name Field */}
            <div className="space-y-2">
                <label className="block text-sm font-medium text-ink">שם {entityLabel}</label>
                <input
                    type="text"
                    className="w-full px-4 py-3 rounded-2xl bg-white border-none shadow-soft focus:outline-none focus:shadow-card focus:bg-surface transition-all duration-200 text-ink-strong placeholder:text-ink-subtle"
                    placeholder="הזן שם"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </div>

            {/* Description Field */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-ink">תיאור {entityLabel}</label>
                    <button
                        type="button"
                        onClick={() => setShowAiDialog(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-brand-50 to-secondary-50
                                 text-brand-700 rounded-lg hover:from-brand-100 hover:to-secondary-100
                                 transition-all duration-200 text-xs font-medium border border-brand-200"
                        title="עזרה מ-AI"
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>AI</span>
                    </button>
                </div>
                <textarea
                    className="w-full px-4 py-3 rounded-2xl bg-white border-none shadow-soft focus:outline-none focus:shadow-card focus:bg-surface transition-all duration-200 text-ink-strong placeholder:text-ink-subtle min-h-[120px] resize-y"
                    placeholder="תאר את העסק או המפגש"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </div>
            {/* Type Selector - Only show for business and event, not for barter */}
            {type !== 'barter' && (
                <div className="space-y-3">
                    <label className="block text-sm font-medium text-ink">סוג הפרסום</label>
                    <div className="flex gap-3">
                        <label className="flex-1 cursor-pointer">
                            <input
                                type="radio"
                                name="type"
                                value="business"
                                checked={type === 'business'}
                                onChange={() => setType('business')}
                                className="sr-only peer"
                            />
                            <div className="px-5 py-3 rounded-2xl bg-white shadow-soft peer-checked:bg-brand-400 peer-checked:text-white peer-checked:shadow-card transition-all duration-200 text-center font-medium text-ink">
                                עסק
                            </div>
                        </label>
                        <label className="flex-1 cursor-pointer">
                            <input
                                type="radio"
                                name="type"
                                value="event"
                                checked={type === 'event'}
                                onChange={() => setType('event')}
                                className="sr-only peer"
                            />
                            <div className="px-5 py-3 rounded-2xl bg-white shadow-soft peer-checked:bg-brand-400 peer-checked:text-white peer-checked:shadow-card transition-all duration-200 text-center font-medium text-ink">
                                מפגש
                            </div>
                        </label>
                    </div>
                </div>
            )}

            {/* Event Time (conditional) */}
            {type === 'event' && (
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-ink">תאריך ושעה של המפגש</label>
                    <input
                        type="datetime-local"
                        className="w-full px-4 py-3 rounded-2xl bg-white border-none shadow-soft focus:outline-none focus:shadow-card focus:bg-surface transition-all duration-200 text-ink-strong"
                        value={eventTime}
                        onChange={(e) => setEventTime(e.target.value)}
                    />
                </div>
            )}
            {/* Phone Number */}
            <div className="space-y-2">
                <label className="block text-sm font-medium text-ink">מספר טלפון</label>
                <input
                    type="text"
                    className="w-full px-4 py-3 rounded-2xl bg-white border-none shadow-soft focus:outline-none focus:shadow-card focus:bg-surface transition-all duration-200 text-ink-strong placeholder:text-ink-subtle"
                    placeholder="050-1234567"
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                />
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={whatsapp}
                        onChange={() => setWhatsapp(!whatsapp)}
                        className="w-5 h-5 rounded border-2 border-line-strong text-brand-400 focus:ring-2 focus:ring-brand-400 focus:ring-offset-0 transition-all"
                    />
                    <span className="text-sm text-ink">זמין ב־WhatsApp</span>
                </label>
            </div>

            {/* Days of Week Selector - Only for business type */}
            {type === 'business' && (
                <div className="space-y-3 bg-white p-5 rounded-2xl shadow-soft">
                    <label className="block text-sm font-medium text-ink">ימי פעילות העסק</label>
                    <div className="text-xs text-ink-subtle">סמנו את הימים בהם העסק פעיל</div>
                    <div className="grid grid-cols-7 gap-2">
                        {[
                            { day: 0, label: 'א', fullName: 'ראשון' },
                            { day: 1, label: 'ב', fullName: 'שני' },
                            { day: 2, label: 'ג', fullName: 'שלישי' },
                            { day: 3, label: 'ד', fullName: 'רביעי' },
                            { day: 4, label: 'ה', fullName: 'חמישי' },
                            { day: 5, label: 'ו', fullName: 'שישי' },
                            { day: 6, label: 'ש', fullName: 'שבת' }
                        ].map((dayInfo) => {
                            const isActive = activeDays.includes(dayInfo.day);
                            return (
                                <button
                                    key={dayInfo.day}
                                    type="button"
                                    onClick={() => {
                                        if (isActive) {
                                            setActiveDays(activeDays.filter(d => d !== dayInfo.day));
                                        } else {
                                            setActiveDays([...activeDays, dayInfo.day].sort());
                                        }
                                    }}
                                    className={`py-2 px-1 rounded-lg font-semibold text-sm transition-all duration-200 ${isActive
                                        ? 'bg-brand-400 text-white shadow-md'
                                        : 'bg-surface-muted text-ink-subtle hover:bg-surface-sunken'
                                        }`}
                                    title={dayInfo.fullName}
                                >
                                    {dayInfo.label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="text-xs text-ink-subtle mt-1">
                        {activeDays.length === 7 ? 'פעיל כל השבוע' :
                            activeDays.length === 0 ? 'לא נבחרו ימים' :
                                `פעיל ${activeDays.length} ימים בשבוע`}
                    </div>
                </div>
            )}

            {/* Location Picker */}
            <div className="space-y-3 bg-white p-5 rounded-2xl shadow-soft">
                <label className="block text-sm font-medium text-ink">מיקום (אופציונלי)</label>

                {!tmpLocation.lat || !tmpLocation.lng ? (
                    <p className="text-sm text-ink-muted">
                        טרם נבחר מיקום — בחר.י נקודה במפה כדי לשמור את מיקום ה{entityLabel}.
                    </p>
                ) : (
                    <p className="text-sm text-brand-400 font-medium">
                        נבחר מיקום: {tmpLocation.lat.toFixed(5)}, {tmpLocation.lng.toFixed(5)}
                    </p>
                )}

                <div className="h-64 relative overflow-hidden rounded-2xl shadow-card">
                    <LocationPicker
                        initialLocation={tmpLocation}
                        onLocationSelect={({ lat, lng }) => {
                            setTmpLocation({ lat, lng });
                        }}
                    />
                </div>

                {/* Address text field */}
                <div className="pt-4 border-t border-line-subtle">
                    <label className="block text-sm font-medium text-ink mb-2">כתובת (לא חובה)</label>
                    <input
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="w-full px-4 py-3 rounded-2xl bg-white border-none shadow-soft focus:outline-none focus:shadow-card focus:bg-surface transition-all duration-200 text-ink-strong placeholder:text-ink-subtle"
                        placeholder="לדוגמה: רחוב הרצל 10, תל אביב"
                    />
                    <div className="text-xs text-ink-subtle mt-1">
                        הזן כתובת מדויקת אם ברצונך להציג אותה למשתמשים
                    </div>
                </div>
            </div>

            {/* Website - Not shown for barters */}
            {type !== 'barter' && (
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-ink">אתר ה{entityLabel}</label>
                    <input
                        type="text"
                        className="w-full px-4 py-3 rounded-2xl bg-white border-none shadow-soft focus:outline-none focus:shadow-card focus:bg-surface transition-all duration-200 text-ink-strong placeholder:text-ink-subtle"
                        placeholder="https://example.com"
                        value={website && website !== 'None' ? website : ''}
                        onChange={(e) => setWebsite(e.target.value)}
                    />
                </div>
            )}

            {/* Founder Profile Section - Optional & Collapsible */}
            <div className="bg-warning-50 border border-warning-100 rounded-2xl shadow-soft p-5 space-y-3">
                <button
                    type="button"
                    onClick={() => setShowFounderProfile(!showFounderProfile)}
                    className="w-full flex items-center justify-between text-sm font-semibold text-warning-700 hover:text-warning-700 transition"
                >
                    <span className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        פרופיל מייסד/ת (אופציונלי)
                    </span>
                    <span className="text-xl">{showFounderProfile ? '−' : '+'}</span>
                </button>
                <p className="text-xs text-warning-700">הוסיפו נגיעה אישית - שתפו את הסיפור שלכם!</p>

                <AnimatePresence>
                    {showFounderProfile && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-3 pt-2"
                        >
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-ink">שם מייסד/ת</label>
                                <input
                                    type="text"
                                    value={founderName}
                                    onChange={(e) => setFounderName(e.target.value)}
                                    className="w-full px-4 py-3 rounded-2xl bg-white border-none shadow-soft focus:outline-none focus:shadow-card focus:bg-surface transition-all duration-200 text-ink-strong placeholder:text-ink-subtle"
                                    placeholder="למשל: יעל כהן"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-ink">אודות המייסד/ת</label>
                                <textarea
                                    value={founderAbout}
                                    onChange={(e) => setFounderAbout(e.target.value)}
                                    className="w-full px-4 py-3 rounded-2xl bg-white border-none shadow-soft focus:outline-none focus:shadow-card focus:bg-surface transition-all duration-200 text-ink-strong placeholder:text-ink-subtle min-h-[80px] resize-y"
                                    rows="3"
                                    placeholder="ספרו בקצרה על עצמכם ועל המסע שהוביל ליצירת העסק..."
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-ink">תמונת פרופיל מייסד/ת</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="block w-full text-sm text-ink bg-white rounded-xl border-2 border-dashed border-line-strong p-4 cursor-pointer hover:border-warning-500 hover:bg-warning-50 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-warning-500 file:text-white hover:file:bg-warning-500"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setFounderImage(file);
                                            const reader = new FileReader();
                                            reader.onload = (event) => {
                                                setFounderImagePreview(event.target.result);
                                            };
                                            reader.readAsDataURL(file);
                                        }
                                    }}
                                />
                                {founderImagePreview && (
                                    <div className="mt-3 flex flex-col items-center gap-2">
                                        <img
                                            src={founderImagePreview}
                                            alt="תצוגה מקדימה של מייסד"
                                            className="w-28 h-28 rounded-full object-cover shadow-lg border-4 border-warning-100"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFounderImage(null);
                                                setFounderImagePreview(null);
                                            }}
                                            className="px-4 py-2 text-sm font-medium text-white bg-danger-500 hover:bg-danger-600 rounded-xl transition-all shadow-md"
                                        >
                                            הסר תמונה
                                        </button>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Categories */}
            <div className="space-y-3">
                <label className="block text-sm font-medium text-ink">בחר קטגוריות (עד 3)</label>
                <div className="bg-white p-4 rounded-2xl shadow-soft max-h-64 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2">
                        {allCategories.map((cat, idx) => (
                            <label key={idx} className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={selectedCategories.includes(cat)}
                                    onChange={() => handleCategoryToggle(cat)}
                                    className="w-5 h-5 rounded border-2 border-line-strong text-brand-400 focus:ring-2 focus:ring-brand-400 focus:ring-offset-0 transition-all"
                                />
                                <span className="text-sm text-ink group-hover:text-brand-400 transition-colors">
                                    {cat}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
                {showCategoryLimitMsg && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
                        <div className="bg-secondary-500 text-white px-6 py-4 rounded-2xl shadow-elevated font-medium text-sm pointer-events-auto animate-bounce">
                            ניתן לבחור עד 3 קטגוריות בלבד
                        </div>
                    </div>
                )}

                <div className="mt-3 space-y-2">
                    <label className="block text-sm font-medium text-ink">או הוסף קטגוריה חדשה</label>
                    <input
                        type="text"
                        value={customCategory}
                        onChange={(e) => setCustomCategory(e.target.value)}
                        placeholder="קטגוריה חדשה "
                        className="w-full px-4 py-3 rounded-2xl bg-white border-none shadow-soft focus:outline-none focus:shadow-card focus:bg-surface transition-all duration-200 text-ink-strong placeholder:text-ink-subtle"
                    />
                </div>
            </div>

            {/* Zones */}
            <div className="space-y-3">
                <label className="block text-sm font-medium text-ink">בחר קהילות (עד 5)</label>
                <div className="bg-white p-4 rounded-2xl shadow-soft max-h-64 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2">
                        {allZones.map((zone, idx) => {
                            const zoneId = typeof zone === 'string' ? zone : zone.zone_id;
                            const zoneDisplay = typeof zone === 'string' ? zone : (zone.zone_display_name || zone.zone_id);
                            return (
                                <label key={idx} className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={selectedZones.includes(zoneId)}
                                        onChange={() => {
                                            if (selectedZones.includes(zoneId)) {
                                                setSelectedZones(selectedZones.filter((z) => z !== zoneId));
                                            } else if (selectedZones.length < 5) {
                                                setSelectedZones([...selectedZones, zoneId]);
                                            } else {
                                                setShowZoneLimitMsg(true);
                                                setTimeout(() => setShowZoneLimitMsg(false), 2000);
                                            }
                                        }}
                                        className="w-5 h-5 rounded border-2 border-line-strong text-brand-400 focus:ring-2 focus:ring-brand-400 focus:ring-offset-0 transition-all"
                                    />
                                    <span className="text-sm text-ink group-hover:text-brand-400 transition-colors">
                                        {zoneDisplay}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>
                {showZoneLimitMsg && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
                        <div className="bg-secondary-500 text-white px-6 py-4 rounded-2xl shadow-elevated font-medium text-sm pointer-events-auto animate-bounce">
                            ניתן לבחור עד 5 אזורים בלבד
                        </div>
                    </div>
                )}

                {/* Custom zones section */}
                <div className="mt-4 pt-4 border-t border-line space-y-3">
                    <label className="block text-sm font-semibold text-brand-700">אזורים מותאמים אישית (בכפוף לאישור מנהל)</label>

                    {/* Display custom zones as chips */}
                    {customZones.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {customZones.map((customZone, index) => (
                                <div key={index} className="flex items-center gap-1.5 bg-brand-100 text-brand-700 px-3 py-1.5 rounded-full text-sm font-medium border border-brand-200">
                                    <span>{customZone}</span>
                                    <button
                                        type="button"
                                        onClick={() => setCustomZones(customZones.filter((_, i) => i !== index))}
                                        className="hover:bg-brand-200 rounded-full p-0.5 transition"
                                        title="הסר אזור"
                                    >
                                        <span className="text-xs">✕</span>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add custom zone input */}
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={customZoneInput}
                            onChange={e => setCustomZoneInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && customZoneInput.trim()) {
                                    e.preventDefault();
                                    const trimmedInput = customZoneInput.trim();
                                    const lowerInput = trimmedInput.toLowerCase();

                                    // Check if zone exists in approved zones
                                    if (allZones.some(z => zoneToString(z).trim().toLowerCase() === lowerInput)) {
                                        alert(`האזור "${trimmedInput}" כבר קיים ברשימה. אנא בחר אותו מהרשימה במקום להוסיף אזור חדש.`);
                                        setCustomZoneInput('');
                                        return;
                                    }

                                    // Check if zone is pending approval
                                    if (pendingZones.some(z => zoneToString(z).trim().toLowerCase() === lowerInput)) {
                                        alert(`האזור "${trimmedInput}" כבר ממתין לאישור. אנא המתן לאישור או בחר אזור אחר.`);
                                        setCustomZoneInput('');
                                        return;
                                    }

                                    const totalZones = selectedZones.length + customZones.length;
                                    if (totalZones < 5 && !customZones.some(z => z.trim().toLowerCase() === lowerInput)) {
                                        setCustomZones([...customZones, trimmedInput]);
                                        setCustomZoneInput('');
                                    }
                                }
                            }}
                            placeholder={
                                selectedZones.length + customZones.length >= 5
                                    ? 'הגעת למגבלת 5 מקומיות'
                                    : "שם אזור חדש"
                            }
                            className="flex-1 px-4 py-3 rounded-2xl bg-white border-none shadow-soft focus:outline-none focus:shadow-card focus:bg-surface transition-all duration-200 text-ink-strong placeholder:text-ink-subtle"
                            maxLength={30}
                            disabled={selectedZones.length + customZones.length >= 5}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                if (customZoneInput.trim()) {
                                    const trimmedInput = customZoneInput.trim();
                                    const lowerInput = trimmedInput.toLowerCase();

                                    // Check if zone exists in approved zones
                                    if (allZones.some(z => zoneToString(z).trim().toLowerCase() === lowerInput)) {
                                        alert(`האזור "${trimmedInput}" כבר קיים ברשימה. אנא בחר אותו מהרשימה במקום להוסיף אזור חדש.`);
                                        setCustomZoneInput('');
                                        return;
                                    }

                                    // Check if zone is pending approval
                                    if (pendingZones.some(z => zoneToString(z).trim().toLowerCase() === lowerInput)) {
                                        alert(`האזור "${trimmedInput}" כבר ממתין לאישור. אנא המתן לאישור או בחר אזור אחר.`);
                                        setCustomZoneInput('');
                                        return;
                                    }

                                    const totalZones = selectedZones.length + customZones.length;
                                    if (totalZones < 5 && !customZones.some(z => z.trim().toLowerCase() === lowerInput)) {
                                        setCustomZones([...customZones, trimmedInput]);
                                        setCustomZoneInput('');
                                    }
                                }
                            }}
                            disabled={!customZoneInput.trim() || selectedZones.length + customZones.length >= 5}
                            className="px-4 py-2 bg-brand-600 text-white rounded-xl font-semibold shadow hover:bg-brand-700 disabled:bg-line disabled:cursor-not-allowed transition"
                        >
                            + הוסף
                        </button>
                    </div>
                    <p className="text-xs text-ink-subtle">
                        סה"כ נבחרו: {selectedZones.length + customZones.length}/5 מקומיות
                    </p>
                </div>
            </div>
            {/* Existing Images Management */}
            <div className="space-y-3">
                <h3 className="text-sm font-medium text-ink">הסר תמונות \ בחר תמונת שער ל{entityLabel}</h3>
                <div className="bg-white p-4 rounded-2xl shadow-soft">
                    <div className="grid grid-cols-2 gap-3">
                        {business.images_urls.map((url, idx) => {
                            const isVideo = url.toLowerCase().includes('.mp4') || url.includes('video');

                            return (
                                <div
                                    key={idx}
                                    className={`relative rounded-2xl overflow-hidden transition-all duration-200 ${selectedCoverImage === url
                                        ? 'ring-4 ring-brand-400 shadow-card'
                                        : 'ring-2 ring-line hover:ring-line-strong'
                                        } ${removedImages.includes(url) ? 'opacity-40' : ''}`}
                                >
                                    <div
                                        onClick={() => setSelectedCoverImage(url)}
                                        className="cursor-pointer bg-surface-muted"
                                    >
                                        {isVideo ? (
                                            <video
                                                src={url}
                                                className="w-full h-32 object-cover"
                                                muted
                                                playsInline
                                                preload="metadata"
                                                onError={() => console.warn(`Failed to load video at ${url}`)}
                                            >
                                                <source src={url} type="video/mp4" />
                                                הדפדפן שלך לא תומך בהצגת וידאו
                                            </video>
                                        ) : (
                                            <img
                                                src={url}
                                                alt={`media-${idx}`}
                                                className="w-full h-32 object-cover"
                                            />
                                        )}
                                    </div>

                                    <button
                                        className={`absolute top-2 right-2 px-3 py-1.5 text-xs font-medium rounded-xl transition-all shadow-md ${removedImages.includes(url)
                                            ? 'bg-brand-400 text-white hover:bg-brand-500'
                                            : 'bg-secondary-500 text-white hover:bg-secondary-500'
                                            }`}
                                        onClick={() => toggleRemoveImage(url)}
                                    >
                                        {removedImages.includes(url) ? 'בטל הסרה' : 'הסר'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <p className="text-xs text-ink-subtle mt-3">סה"כ מותר עד 5 תמונות. לחץ על תמונה כדי להגדיר כתמונת שער.</p>
                </div>
            </div>

            {/* New Media Upload */}
            <div className="space-y-3">
                <label className="block text-sm font-medium text-ink">תמונות ל{entityLabel} (עד 5 תמונות)</label>

                <div className="bg-white p-4 rounded-2xl shadow-soft">
                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="block w-full text-sm text-ink bg-surface-muted rounded-xl border-2 border-dashed border-line-strong p-4 cursor-pointer hover:border-brand-400 hover:bg-surface transition-all file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-brand-400 file:text-white hover:file:bg-brand-500"
                        onChange={(e) => {
                            const selectedFiles = Array.from(e.target.files);
                            const remainingExisting = business.images_urls.filter((url) => !removedImages.includes(url));
                            const maxUploadable = 5 - remainingExisting.length;

                            if (maxUploadable <= 0) {
                                alert("אפשר להעלות עד 5 תמונות בלבד. הסר.י קודם תמונות ישנות כדי להוסיף חדשות.");
                                return;
                            }

                            const validFiles = [];
                            const previews = [];

                            for (const file of selectedFiles) {
                                validFiles.push(file);
                                if (validFiles.length === maxUploadable) break;
                            }

                            setImages(validFiles);

                            validFiles.forEach((file, index) => {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                    previews[index] = {
                                        type: "image",
                                        url: event.target.result,
                                    };
                                    if (previews.filter(Boolean).length === validFiles.length) {
                                        setImagePreviews(previews);
                                    }
                                };
                                reader.readAsDataURL(file);
                            });
                        }}
                    />

                    {images.length > 0 && (
                        <p className="text-brand-400 text-sm mt-3 font-medium">{images.length} קבצים נבחרו</p>
                    )}

                    {imagePreviews.length > 0 && (
                        <div className="grid grid-cols-2 gap-3 mt-4">
                            {imagePreviews.map((preview, idx) => (
                                preview.type === "image" ? (
                                    <img
                                        key={idx}
                                        src={preview.url}
                                        alt={`preview ${idx + 1}`}
                                        className="w-full h-32 object-cover rounded-2xl shadow-soft"
                                    />
                                ) : (
                                    <video
                                        key={idx}
                                        src={preview.url}
                                        controls
                                        className="w-full h-32 object-cover rounded-2xl shadow-soft"
                                    />
                                )
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-4">
                <button
                    onClick={handleSubmit}
                    disabled={loading || saveSuccess}
                    className={`w-full px-6 py-4 rounded-2xl font-semibold text-base shadow-soft transition-all duration-200 flex justify-center items-center gap-2 ${loading
                        ? 'bg-line text-ink-muted cursor-wait'
                        : saveSuccess
                            ? 'bg-brand-400 text-white shadow-card'
                            : 'bg-brand-400 text-white hover:bg-brand-500 hover:shadow-card hover:-translate-y-0.5 active:translate-y-0'
                        }`}
                >
                    {loading ? (
                        <>
                            <svg className="animate-spin h-5 w-5 text-ink-muted" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            <span>שומר...</span>
                        </>
                    ) : saveSuccess ? (
                        <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>נשמר</span>
                        </>
                    ) : (
                        'שמור שינויים'
                    )}
                </button>

                <button
                    onClick={onCancel}
                    disabled={loading}
                    className="w-full px-6 py-4 rounded-2xl bg-white text-ink font-medium text-base shadow-soft hover:shadow-card hover:bg-surface-muted transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    ביטול
                </button>
            </div>

            {/* AI Description Dialog */}
            {showAiDialog && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
                    <AiDescriptionDialog
                        initialText={description}
                        entityType={type}
                        onApply={(newText) => setDescription(newText)}
                        onClose={() => setShowAiDialog(false)}
                        extraContext={{
                            name: name,
                            category: selectedCategories.join(', ')
                        }}
                        chatId={chatId}
                    />
                </div>
            )}
        </div>
    );
};
